import React, { useMemo } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { HoverUnderlineSpan } from "@/components/ui/HoverUnderlineSpan";
import { useFormatting } from "@/hooks/useFormatting";
import { useDataSourceContext } from "@/contexts/DataSourceContext";
import { getDashboardMetrics } from "@/services/metricsService";
import { useDrillDown, generate30DayForecastDrillDown } from "@/hooks/useDrillDown";
import { usePermissions } from "@/hooks/usePermissions";
import { useApp } from "@/contexts/AppContext";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useTerminology } from "@/hooks/useTerminology";

export default function Ceo30DayForecastCard() {
  const { hasPermission } = usePermissions();
  const { getForecastTerm } = useTerminology();
  const { formatCurrency } = useFormatting();
  const { dataSources } = useDataSourceContext();
  const { showDrillDown } = useDrillDown();
  const { mode } = useApp();
  const { metrics: forecastMetrics, isLoading, error } = useBaseForecast();

  const demoMetrics = useMemo(() => {
    if (mode === "live") return null;
    return getDashboardMetrics(dataSources);
  }, [dataSources, mode]);

  const liveValues = useMemo(() => {
    if (mode !== "live" || !forecastMetrics) return null;
    return {
      currentCash: forecastMetrics.startingCash,
      projectedCash30Days: forecastMetrics.startingCash + forecastMetrics.avgMonthlyInflow - forecastMetrics.avgMonthlyOutflow,
      expected30DayIncome: forecastMetrics.avgMonthlyInflow,
      expected30DayExpenses: forecastMetrics.avgMonthlyOutflow,
    };
  }, [mode, forecastMetrics]);

  if (!hasPermission("view_executive_cash_position_card")) return null;

  if (mode === "live" && isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading {getForecastTerm(false)} data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (mode === "live" && error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  const currentCash = mode === "live" ? liveValues?.currentCash ?? 0 : demoMetrics?.currentCash ?? 0;
  const projectedCash30Days = mode === "live" ? liveValues?.projectedCash30Days ?? 0 : demoMetrics?.projectedCash30Days ?? 0;
  const expected30DayIncome = mode === "live" ? liveValues?.expected30DayIncome ?? 0 : demoMetrics?.expected30DayIncome ?? 0;
  const expected30DayExpenses = mode === "live" ? liveValues?.expected30DayExpenses ?? 0 : demoMetrics?.expected30DayExpenses ?? 0;

  const change = projectedCash30Days - currentCash;
  const changePercent = currentCash !== 0 ? (change / Math.abs(currentCash)) * 100 : 0;
  const isPositive = changePercent >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  const formatPercentage = (value: number) => {
    const formatted = Math.abs(value).toFixed(1);
    return isPositive ? `+${formatted}%` : `-${formatted}%`;
  };

  return (
    <Card className="h-full" style={{ borderLeft: '3px solid var(--color-info-400)' }}>
      <CardContent className="p-4 flex flex-col h-full">
        {/* Top row — title + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">30-Day {getForecastTerm(true)}</span>
            <InfoTooltip
              title={`30-Day ${getForecastTerm(true)}`}
              content="Projected cash position 30 days from now based on outstanding invoices, bills, and scheduled payments."
            />
          </div>
          <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-info-100)' }}>
            <TrendIcon className="h-4 w-4" style={{ color: 'var(--color-info-600)' }} />
          </div>
        </div>

        {/* Value */}
        <div className="mt-3">
          <HoverUnderlineSpan
            className="text-2xl font-bold text-neutral-900 tracking-tight cursor-pointer"
            onClick={() =>
              showDrillDown(
                "forecast-point",
                generate30DayForecastDrillDown(
                  projectedCash30Days,
                  currentCash,
                  expected30DayIncome,
                  expected30DayExpenses,
                ),
              )
            }
          >
            {formatCurrency(projectedCash30Days)}
          </HoverUnderlineSpan>
        </div>

        {/* Sub-text */}
        <div className="mt-1 flex items-center gap-1">
          <span className="text-xs font-semibold text-neutral-800">{formatPercentage(changePercent)}</span>
          <span className="text-xs font-normal text-neutral-800">vs current</span>
        </div>
      </CardContent>
    </Card>
  );
}
